class AbasSolucoesSection {
    constructor() {
        this.scrollFrame = null;
        this.initialNavigation = null;
        this.components = [
            ...document.querySelectorAll('[data-module="abas_solucoes_section"]'),
        ]
            .map(component => this.bind(component))
            .filter(Boolean);

        if (!this.components.length) {
            return;
        }

        this.bindFocusTracking();
        this.syncFromLocation();
        this.restoreInitialPosition();

        const onLocationChange = () => {
            this.cancelInitialNavigation();
            this.syncFromLocation();
        };

        window.addEventListener('hashchange', onLocationChange);
        window.addEventListener('popstate', onLocationChange);
    }

    bind(component) {
        if (component.dataset.tabsInitialized === 'true') {
            return null;
        }

        const buttons = [...component.querySelectorAll('[data-tab-btn]')];
        const panels = [...component.querySelectorAll('[data-tab-panel]')];
        const select = component.querySelector('[data-tab-select]');
        const valid = buttons.length
            && buttons.length === panels.length
            && buttons.every(button => panels.some(panel => (
                panel.id
                && panel.dataset.tabPanel === button.dataset.tabBtn
            )));

        if (!valid) {
            return null;
        }

        const state = {
            component,
            buttons,
            panels,
            select,
            controls: [...buttons, select].filter(Boolean),
            focusedControl: null,
            active: null,
            desktop: window.matchMedia('(min-width: 1024px)'),
        };

        component.dataset.tabsInitialized = 'true';

        buttons.forEach((button, index) => {
            const panel = panels.find(item => (
                item.dataset.tabPanel === button.dataset.tabBtn
            ));

            button.setAttribute('aria-controls', panel.id);
            button.addEventListener('click', () => {
                this.selectTab(state, button.dataset.tabBtn);
            });
            button.addEventListener('keydown', event => {
                let nextIndex;

                switch (event.key) {
                    case 'ArrowRight':
                        nextIndex = (index + 1) % buttons.length;
                        break;
                    case 'ArrowLeft':
                        nextIndex = (index - 1 + buttons.length) % buttons.length;
                        break;
                    case 'Home':
                        nextIndex = 0;
                        break;
                    case 'End':
                        nextIndex = buttons.length - 1;
                        break;
                    default:
                        return;
                }

                event.preventDefault();
                buttons[nextIndex].focus({ preventScroll: true });
                this.selectTab(state, buttons[nextIndex].dataset.tabBtn);
            });
        });

        select?.addEventListener('change', () => {
            this.selectTab(state, select.value);
        });

        state.desktop.addEventListener('change', () => {
            const activeButton = buttons.find(button => (
                button.dataset.tabBtn === state.active
            ));
            const focused = document.activeElement;
            const documentFocused = !focused
                || focused === document.body
                || focused === document.documentElement;
            const previous = state.controls.includes(focused)
                ? focused
                : documentFocused
                    ? state.focusedControl
                    : null;

            if (!previous) {
                return;
            }

            if (state.desktop.matches && previous === select) {
                activeButton?.focus({ preventScroll: true });
            } else if (!state.desktop.matches && buttons.includes(previous)) {
                select?.focus({ preventScroll: true });
            }
        });

        return state;
    }

    bindFocusTracking() {
        document.addEventListener('focusin', event => {
            this.components.forEach(state => {
                state.focusedControl = state.controls.includes(event.target)
                    ? event.target
                    : null;
            });
        });
        document.addEventListener('focusout', event => {
            if (!event.relatedTarget) {
                return;
            }

            this.components.forEach(state => {
                state.focusedControl = state.controls.includes(event.relatedTarget)
                    ? event.relatedTarget
                    : null;
            });
        });
        document.addEventListener('pointerdown', event => {
            this.components.forEach(state => {
                const inside = state.controls.some(control => (
                    control.contains(event.target)
                ));

                if (!inside) {
                    state.focusedControl = null;
                }
            });
        }, { capture: true, passive: true });
    }

    fragment() {
        try {
            return decodeURIComponent(window.location.hash.slice(1));
        } catch {
            return null;
        }
    }

    syncFromLocation({ scroll = true } = {}) {
        this.cancelScroll();

        const fragment = this.fragment();
        const owner = this.components.find(state => (
            state.panels.some(panel => panel.id === fragment)
        ));

        this.components.forEach(state => {
            const target = state === owner
                ? state.panels.find(panel => panel.id === fragment)
                : null;
            const slug = target?.dataset.tabPanel
                ?? (owner && state.active)
                ?? state.buttons[0].dataset.tabBtn;

            this.activate(state, slug);
        });

        if (scroll && owner) {
            this.scrollToNavigation(owner);
        }
    }

    selectTab(state, slug) {
        this.cancelInitialNavigation();

        const panel = state.panels.find(item => item.dataset.tabPanel === slug);

        if (!panel) {
            return;
        }

        const changed = state.active !== slug;

        if (this.fragment() !== panel.id) {
            const url = new URL(window.location.href);
            url.hash = panel.id;
            window.history.pushState(window.history.state, '', url);
        }

        this.syncFromLocation({ scroll: false });

        if (changed) {
            this.scrollToNavigation(state);
        }
    }

    activate(state, slug) {
        const activePanel = state.panels.find(panel => (
            panel.dataset.tabPanel === slug
        ));

        if (!activePanel) {
            return;
        }

        const moveFocus = state.panels.some(panel => (
            panel !== activePanel
            && panel.contains(document.activeElement)
        ));

        state.active = slug;
        state.buttons.forEach(button => {
            const active = button.dataset.tabBtn === slug;
            button.setAttribute('aria-selected', String(active));
            button.tabIndex = active ? 0 : -1;
        });
        state.panels.forEach(panel => {
            const active = panel === activePanel;
            panel.hidden = !active;
            panel.inert = !active;
            panel.classList.toggle('hidden', !active);
            panel.setAttribute('aria-hidden', String(!active));
        });

        if (state.select) {
            state.select.value = slug;
            state.select.setAttribute('aria-controls', activePanel.id);
        }

        if (moveFocus) {
            const activeControl = state.desktop.matches
                ? state.buttons.find(button => button.dataset.tabBtn === slug)
                : state.select;

            activeControl?.focus({ preventScroll: true });
        }
    }

    cancelScroll() {
        if (this.scrollFrame !== null) {
            window.cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = null;
        }
    }

    restoreInitialPosition() {
        const fragment = this.fragment();
        const owner = this.components.find(state => (
            state.panels.some(panel => panel.id === fragment)
        ));

        if (!owner) {
            return;
        }

        const navigation = {
            href: window.location.href,
            interrupt: () => this.cancelInitialNavigation(),
            events: ['pointerdown', 'keydown', 'wheel', 'touchstart'],
        };

        this.initialNavigation = navigation;
        navigation.events.forEach(event => {
            window.addEventListener(event, navigation.interrupt, {
                capture: true,
                passive: true,
            });
        });

        const loaded = document.readyState === 'complete'
            ? Promise.resolve()
            : new Promise(resolve => {
                window.addEventListener('load', resolve, { once: true });
            });

        loaded
            .then(() => document.fonts?.ready)
            .then(() => {
                if (this.initialNavigation !== navigation) {
                    return;
                }

                const unchanged = window.location.href === navigation.href;

                this.cancelInitialNavigation();

                if (unchanged) {
                    this.syncFromLocation();
                }
            });
    }

    cancelInitialNavigation() {
        const navigation = this.initialNavigation;

        if (!navigation) {
            return;
        }

        this.initialNavigation = null;
        this.cancelScroll();
        navigation.events.forEach(event => {
            window.removeEventListener(event, navigation.interrupt, true);
        });
    }

    scrollToNavigation(state) {
        this.cancelScroll();
        this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = null;

            const header = document.querySelector('[data-module="header"]');
            const bar = header?.querySelector('[data-header-bar]');
            const headerBottom = Math.max(
                0,
                bar?.getBoundingClientRect().bottom ?? 0
            );
            const top = Math.max(
                0,
                window.scrollY
                    + state.component.getBoundingClientRect().top
                    - headerBottom
                    - 24
            );

            if (Math.abs(window.scrollY - top) < 1) {
                return;
            }

            window.scrollTo({
                top,
                left: window.scrollX,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ? 'instant'
                    : 'smooth',
            });
        });
    }
}

new AbasSolucoesSection();

export default AbasSolucoesSection;
